import { useEffect, useRef, useState } from 'react';
import { useDeviceDataStore, type AiDetectionEvent } from '../../stores/deviceDataStore';
import { ensureDeviceSocket } from '../../services/deviceSocket';

const WHEP_READER_SRC = '/mediamtx-reader.js';

/** 弹窗自动关闭时间（ms）；测试可用 localStorage.aiAlertAutoCloseMs 覆盖 */
function autoCloseMs(): number {
  const v = Number(localStorage.getItem('aiAlertAutoCloseMs') || '');
  return Number.isFinite(v) && v > 0 ? v : 15000;
}

interface CameraInfo {
  imei: string;
  webrtcUrl?: string;
  hlsUrl?: string;
  deviceName?: string;
}

let streamCache: { ts: number; map: Record<string, CameraInfo> } = { ts: 0, map: {} };

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[data-whep="${src}"]`)) {
      resolve();
      return;
    }
    const s = document.createElement('script');
    s.src = src;
    s.dataset.whep = src;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('WebRTC 播放器加载失败'));
    document.head.appendChild(s);
  });
}

/** 从相机列表接口取 imei → 流地址映射（缓存 30s；无权限/失败降级为空映射） */
async function fetchCameraMap(): Promise<Record<string, CameraInfo>> {
  if (Date.now() - streamCache.ts < 30_000 && Object.keys(streamCache.map).length > 0) {
    return streamCache.map;
  }
  try {
    const token = localStorage.getItem('dtwin_token') || '';
    const resp = await fetch('/api/v1/rtu/devices/cameras', {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!resp.ok) return {};
    const data = (await resp.json()) as { items?: Array<{ imei: string; deviceName?: string; remark?: string }> };
    const map: Record<string, CameraInfo> = {};
    for (const d of data.items || []) {
      const remark = d.remark || '';
      const webrtc = remark.match(/webrtc:\/\/[^\s|]+/i)?.[0];
      const hls = remark.match(/https?:\/\/[^\s|]+\.m3u8[^\s|]*/i)?.[0];
      map[d.imei] = { imei: d.imei, deviceName: d.deviceName, webrtcUrl: webrtc, hlsUrl: hls };
    }
    streamCache = { ts: Date.now(), map };
    return map;
  } catch {
    return {};
  }
}

export default function AiCameraAlertOverlay() {
  const aiEvents = useDeviceDataStore((s) => s.aiEvents);
  const [current, setCurrent] = useState<AiDetectionEvent | null>(null);
  const [camera, setCamera] = useState<CameraInfo | null>(null);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState('');
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const readerRef = useRef<{ close: () => void } | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 自包含：挂载即确保设备 WS 连接（不依赖宿主是否调用 ensureDeviceSocket）
  useEffect(() => {
    ensureDeviceSocket();
  }, []);

  const stopPlayback = () => {
    readerRef.current?.close();
    readerRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setPlaying(false);
  };

  const dismiss = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    stopPlayback();
    setCurrent(null);
    setCamera(null);
    setError('');
  };

  useEffect(() => {
    if (!current) return;
    let cancelled = false;
    const start = async () => {
      const map = await fetchCameraMap();
      if (cancelled) return;
      const info = map[current.deviceId];
      setCamera(info || null);
      if (!info?.webrtcUrl) {
        setError('该相机未登记 WebRTC 流地址');
        return;
      }
      try {
        await loadScript(WHEP_READER_SRC);
        if (cancelled) return;
        const video = videoRef.current;
        if (!video) return;
        const httpUrl = info.webrtcUrl.replace(/^webrtc:\/\//i, 'http://').replace(/\/$/, '');
        const whepUrl = `${httpUrl}/whep`;
        const Reader: any = (window as any).MediaMTXWebRTCReader;
        if (!Reader) throw new Error('WebRTC 播放器加载失败');
        const reader = new Reader({
          url: whepUrl,
          onTrack: (evt: any) => {
            const stream = evt?.streams?.[0] ?? evt?.track;
            if (stream) {
              video.srcObject = stream;
              video.play?.().catch(() => undefined);
              setPlaying(true);
            }
          },
          onError: (err: string) => {
            if (!cancelled) setError(err || 'WebRTC 连接失败');
          },
        });
        readerRef.current = reader;
      } catch (e) {
        if (!cancelled) setError((e as Error).message || '播放失败');
      }
    };
    start();
    return () => {
      cancelled = true;
      stopPlayback();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  useEffect(() => {
    if (aiEvents.length > 0) {
      const latest = aiEvents[0];
      if (!current || latest.ts !== current.ts || latest.deviceId !== current.deviceId) {
        setCurrent(latest);
        setError('');
        if (closeTimer.current) clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(dismiss, autoCloseMs());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiEvents]);

  useEffect(() => () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    stopPlayback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!current) return null;

  return (
    <div
      data-testid="ai-camera-alert"
      style={{
        position: 'fixed',
        right: 24,
        bottom: 24,
        width: 360,
        zIndex: 9999,
        background: '#0f172a',
        border: '1px solid #334155',
        borderRadius: 10,
        boxShadow: '0 8px 30px rgba(0,0,0,0.45)',
        overflow: 'hidden',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#1e293b', color: '#fbbf24' }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>
          AI 检测告警{camera?.deviceName ? ` · ${camera.deviceName}` : ` · ${current.deviceId}`}
        </span>
        <button
          onClick={dismiss}
          style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 14, padding: '2px 6px' }}
          title="关闭"
        >
          ✕
        </button>
      </div>
      <div style={{ position: 'relative', aspectRatio: '4/3', background: '#000' }}>
        <video
          ref={videoRef}
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          muted
          autoPlay
          playsInline
        />
        {!playing && !error && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontSize: 12 }}>连接视频中…</div>}
        {error && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f87171', fontSize: 12, padding: 12, textAlign: 'center' }}>{error}</div>}
      </div>
      <div style={{ padding: '6px 12px', fontSize: 12, color: '#94a3b8', display: 'flex', justifyContent: 'space-between' }}>
        <span>
          检测到 {current.count} 个目标 @ {current.fps} FPS
        </span>
        <span>{current.model || ''}</span>
      </div>
    </div>
  );
}